import { AutoComplete, AutoCompleteProps, Avatar } from 'antd';
import { IconBellOff, IconCheck, IconSearch, IconSelect, IconUser, IconX } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { customersDetailsQueryOptions } from '../../utils/queryOptions';
import { useEffect, useState } from 'react';
import { apiV1 } from '../../utils/apiClient';
import { Customer, ShopNewsRecipient } from '@next-is-free-monorepo/next-is-free-types';
import NAvatar from '../../../../libs/nif-web-components/src/microcomponents/NAvatar';
import { CheckmarkBadge01Icon, NotificationOff01Icon, SchoolBell01Icon, UserIcon } from '@hugeicons/core-free-icons';


interface CustomersAutocompleteProps {
  selectedCustomers: ShopNewsRecipient['recipients'];
  setSelectedCustomers: (selected: ShopNewsRecipient['recipients']) => void;
  disableNoNotifications?: boolean;
}

const CustomersAutocomplete = ({
                                 setSelectedCustomers,
                                 selectedCustomers,
                                 disableNoNotifications
                               }: CustomersAutocompleteProps) => {

  const [search, setSearch] = useState('');

  const customers = useQuery(
    {
      placeholderData: [],
      queryKey: ['customers', 'search', { search }],
      queryFn: async () => {
        const results = await apiV1.get<Customer[]>(`/shop/customers/details?search=${search}`);
        return results.map((item) => {
          const isSelected = selectedCustomers.some((s) => s.id === item.id);
          const isDisabled = !!(disableNoNotifications && !item.notifications_allowed);

          return {
            key: item.id,
            value: item.id,
            label: item.display_name ?? '@' + item.username,
            isSelected,
            isDisabled,
            disabled: isSelected || isDisabled
          };
        });
      },
      enabled: !!search
    }
  );

  return (
    <div>
      <AutoComplete
        optionRender={(value) => <CustomerRow
          label={value.data.label as string}
          isSelected={value.data.isSelected}
          isDisabled={value.data.isDisabled}
        />}
        options={customers.data}
        value={search}
        onSelect={(value, option) => {
          setSelectedCustomers([...selectedCustomers, { id: option.value, label: option.label }]);
          setSearch('');
        }}
        onSearch={(value) => setSearch(value)} className={'w-full'}
        prefix={<IconSearch size={13} className={'text-slate-300'} />}
        notFoundContent={<p className={'text-slate-400'}>Nessun Cliente trovato</p>}
        placeholder={'Cerca nome o username'} />
      <div className={'flex flex-wrap gap-2 mt-2'}>
        {selectedCustomers.map((selectedCustomer) => (
          <div key={selectedCustomer.id} className={'px-0.5 py-0.5 rounded-full border flex items-center gap-2'}>
            <CustomerRow isDisabled={false} isSelected={false} label={selectedCustomer.label} />
            <IconX
              onClick={() => {
                const next = selectedCustomers.filter(item => item.id !== selectedCustomer.id);
                setSelectedCustomers(next);
              }}
              size={14}
              className="mr-2 text-slate-300 hover:text-black cursor-pointer"
            />
          </div>
        ))}
      </div>
    </div>
  );
};
export default CustomersAutocomplete;

function CustomerRow({ label, isSelected, isDisabled }: { label: string; isSelected: boolean; isDisabled: boolean }) {
  return (
    <div
      className={`flex gap-2 items-center line-clamp-1 overflow-hidden ${
        isDisabled ? 'opacity-50 cursor-not-allowed' : ''
      }`}
    >
      <NAvatar
        hugeIcon={isDisabled ? NotificationOff01Icon: UserIcon}
        size="xs"
        color={isSelected ? 'tertiary' : isDisabled ? 'tertiary': 'primary'}
      />
      {label}
    </div>
  );
}
